"use client";
import { useCallback } from "react";
import Link from "next/link";
import { Heart, Star, Plus, Minus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useFavourites } from "@/context/FavouritesContext";
import { useAuth } from "@/context/AuthContext";
import type { Product } from "@/types";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import LazyImage from "@/components/LazyImage";

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const { user } = useAuth();
  const { addToCart, updateQuantity, getItemQuantity, getCartItemId } = useCart();
  const { isFavourite, toggleFavourite } = useFavourites();

  const productImage = product.images?.[0] ?? product.image ?? null;
  const productId = product.id;
  const quantity = getItemQuantity(productId, productId);
  const cartItemId = getCartItemId(productId, productId);

  const handleAdd = useCallback(async () => {
    if (!user) { toast.error("Please login to add items to cart"); return; }
    if (product.blocked || product.outOfStock) {
      toast.error(product.outOfStock ? "This item is out of stock" : "This item is currently unavailable");
      return;
    }
    await addToCart(product, { id: product.id, price: product.price, stock: 999 } as { id: string; price: number; stock: number });
    toast.success(`${product.name} added to cart`);
  }, [user, product, addToCart]);

  const handleIncrease = useCallback(async () => { if (!cartItemId) return; await updateQuantity(cartItemId, quantity + 1); }, [cartItemId, quantity, updateQuantity]);
  const handleDecrease = useCallback(async () => { if (!cartItemId) return; await updateQuantity(cartItemId, quantity - 1); }, [cartItemId, quantity, updateQuantity]);

  const handleFavourite = useCallback(async () => {
    if (!user) { toast.error("Please login to add favourites"); return; }
    await toggleFavourite(product);
  }, [user, product, toggleFavourite]);

  const fav = isFavourite(product.id);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      whileHover={{ y: -2 }}
      className="bg-card rounded-[32px] border border-border shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
    >
      <div className="flex gap-4 p-4">
        {/* Product info - Wrapped in Link */}
        <Link
          href={`/product/${product.id}`}
          className="flex-1 min-w-0 flex flex-col justify-between group/info"
        >
          <div>
            {product.special && (
              <div className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 bg-bestseller rounded-full shadow-sm">
                <Star className="w-3 h-3 text-bestseller-foreground fill-bestseller-foreground" />
                <span className="text-[10px] font-bold text-bestseller-foreground uppercase tracking-wide">Bestseller</span>
              </div>
            )}
            <h3 className="font-semibold text-foreground text-sm md:text-base leading-snug line-clamp-2 group-hover/info:text-primary transition-colors">
              {product.name}
            </h3>
            {product.description && (
              <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">{product.description}</p>
            )}
          </div>
          <div className="mt-3 space-y-0.5">
            <p className="text-lg font-bold text-foreground">₹{product.price}</p>
            <div className="flex items-center gap-2">
              {product.serves && (
                <span className="text-[11px] font-medium text-foreground bg-muted px-1.5 py-0.5 rounded">
                  Serves {product.serves}
                </span>
              )}
              {product.weight && (
                <span className="text-[11px] font-medium text-foreground bg-muted px-1.5 py-0.5 rounded">
                  {product.weight}
                </span>
              )}
            </div>
          </div>
        </Link>

        {/* Image + controls */}
        <div className="flex flex-col items-center gap-2.5 flex-shrink-0">
          <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-2xl overflow-hidden bg-muted aspect-square">
            <Link href={`/product/${product.id}`} className="absolute inset-0">
              {productImage ? (
                <LazyImage
                  src={productImage}
                  alt={product.name}
                  fetchPriority="high"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  wrapperClassName="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-3xl">🍖</div>
              )}
            </Link>

            {/* Favourite button */}
            <motion.button
              onClick={handleFavourite}
              title="Toggle favourite"
              whileTap={{ scale: 0.8 }}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-card/90 backdrop-blur-sm shadow-md hover:shadow-lg transition-all z-10"
            >
              <motion.div
                animate={fav ? { scale: [1, 1.3, 1] } : { scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Heart
                  className={`w-4 h-4 transition-colors duration-200 ${fav ? "text-primary fill-primary" : "text-muted-foreground"
                    }`}
                />
              </motion.div>
            </motion.button>

            {/* Unavailable overlay */}
            {/* Unavailable / Out of Stock overlay */}
            {(product.blocked || product.outOfStock) && (
              <div className="absolute inset-0 bg-foreground/60 backdrop-blur-[2px] flex items-center justify-center z-10">
                <span className={`text-[10px] font-bold text-primary-foreground ${product.outOfStock ? "bg-orange-700" : "bg-destructive"} px-3 py-1 rounded-full uppercase tracking-wide shadow-lg`}>
                  {product.outOfStock ? "Out of Stock" : "Unavailable"}
                </span>
              </div>
            )}
          </div>

          {/* Add / Quantity controls */}
          {!(product.blocked || product.outOfStock) && (
            <AnimatePresence mode="wait">
              {quantity > 0 ? (
                <motion.div
                  key="qty"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center border-2 border-primary rounded-2xl overflow-hidden bg-card shadow-sm"
                >
                  <button
                    onClick={handleDecrease}
                    title="Decrease quantity"
                    className="px-3 py-2 hover:bg-primary/10 active:bg-primary/20 transition-colors"
                  >
                    <Minus className="w-3.5 h-3.5 text-primary" />
                  </button>
                  <motion.span
                    key={quantity}
                    initial={{ scale: 1.3 }}
                    animate={{ scale: 1 }}
                    className="px-3 py-2 text-sm font-bold text-primary min-w-[32px] text-center"
                  >
                    {quantity}
                  </motion.span>
                  <button
                    onClick={handleIncrease}
                    title="Increase quantity"
                    className="px-3 py-2 hover:bg-primary/10 active:bg-primary/20 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5 text-primary" />
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  key="add"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAdd}
                  className="flex items-center gap-1.5 px-5 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-xl shadow-md hover:shadow-lg hover:bg-primary/90 active:scale-95 transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  ADD
                </motion.button>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
